import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  FolderOpen,
  LayoutList,
  Plus,
  Sun,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { TAREFA_STATUS_LABELS, type TarefaGrupoDto, type TarefaStatus } from "@/services/tarefas";

export type TarefasNavView = "hoje" | "todas" | "calendario";

interface TarefasSidebarProps {
  statusFilter: TarefaStatus | "";
  grupoFilter: string;
  navView: TarefasNavView;
  userName: string;
  grupos: TarefaGrupoDto[];
  onNovaTarefa: () => void;
  onNovoGrupo: () => void;
  onExcluirGrupo?: (grupo: TarefaGrupoDto) => void;
  onStatusFilterChange: (status: TarefaStatus | "") => void;
  onGrupoFilterChange: (grupoId: string) => void;
  onNavViewChange: (view: TarefasNavView) => void;
  /** Fecha o drawer em telas pequenas (opcional). */
  onClose?: () => void;
  className?: string;
}

const FILTER_OPTIONS: Array<{
  value: TarefaStatus | "";
  label: string;
  dotClass?: string;
}> = [
  { value: "", label: "Todas" },
  { value: "em_andamento", label: TAREFA_STATUS_LABELS.em_andamento, dotClass: "bg-blue-500" },
  { value: "concluida", label: TAREFA_STATUS_LABELS.concluida, dotClass: "bg-emerald-500" },
  { value: "parada", label: TAREFA_STATUS_LABELS.parada, dotClass: "bg-amber-500" },
  { value: "pendente", label: TAREFA_STATUS_LABELS.pendente, dotClass: "bg-slate-300" },
];

const NAV_ITEMS: Array<{
  id: TarefasNavView;
  label: string;
  icon: typeof Sun;
}> = [
  { id: "hoje", label: "Meu dia", icon: Sun },
  { id: "todas", label: "Todas as tarefas", icon: LayoutList },
  { id: "calendario", label: "Calendário", icon: CalendarDays },
];

export function TarefasSidebar({
  statusFilter,
  grupoFilter,
  navView,
  userName,
  grupos,
  onNovaTarefa,
  onNovoGrupo,
  onExcluirGrupo,
  onStatusFilterChange,
  onGrupoFilterChange,
  onNavViewChange,
  onClose,
  className,
}: TarefasSidebarProps) {
  const initial = userName.trim().charAt(0).toUpperCase() || "?";
  const isCalendario = navView === "calendario";

  const pickView = (view: TarefasNavView) => {
    onNavViewChange(view);
    onClose?.();
  };

  return (
    <aside
      className={cn(
        "flex w-72 max-w-[85vw] flex-shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white lg:w-60",
        className,
      )}
    >
      {onClose && (
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 lg:hidden">
          <span className="text-sm font-semibold text-slate-700">Menu</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className="border-b border-slate-100 p-4">
        <button
          type="button"
          onClick={onNovaTarefa}
          className={cn(
            "flex w-full items-center justify-between rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors",
            isCalendario ? "bg-violet-600 hover:bg-violet-700" : "bg-blue-600 hover:bg-blue-700",
          )}
        >
          <span className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {isCalendario ? "Novo evento" : "Nova tarefa"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </button>
      </div>

      <nav className="space-y-0.5 px-3 py-3">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => pickView(item.id)}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              navView === item.id
                ? item.id === "calendario"
                  ? "bg-violet-50 text-violet-700"
                  : "bg-blue-50 text-blue-700"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-800",
            )}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {item.label}
          </button>
        ))}
      </nav>

      {/* Filtro por status */}
      <div className="mt-2 border-t border-slate-100 px-4 py-4">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Status
        </p>
        <div className="space-y-0.5">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value || "todas"}
              type="button"
              onClick={() => onStatusFilterChange(opt.value)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors",
                statusFilter === opt.value
                  ? "bg-slate-100 font-medium text-slate-800"
                  : "text-slate-600 hover:bg-slate-50",
              )}
            >
              {opt.value === "" ? (
                <CheckCircle2
                  className={cn(
                    "h-3.5 w-3.5",
                    statusFilter === "" ? "text-blue-600" : "text-transparent",
                  )}
                />
              ) : (
                <span className={cn("h-2 w-2 rounded-full", opt.dotClass)} />
              )}
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grupos de tarefas — criados pelo usuário */}
      <div className="border-t border-slate-100 px-4 py-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Grupos de tarefas
          </p>
          <button
            type="button"
            onClick={onNovoGrupo}
            className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
            title="Criar novo grupo"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mb-2 text-[11px] leading-snug text-slate-400">
          Organize por obra, equipe ou escritório.
        </p>
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={() => onGrupoFilterChange("")}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors",
              grupoFilter === ""
                ? "bg-slate-100 font-medium text-slate-800"
                : "text-slate-600 hover:bg-slate-50",
            )}
          >
            <FolderOpen className="h-3.5 w-3.5 text-slate-400" />
            Todos
          </button>
          {grupos.length === 0 ? (
            <p className="px-2 py-1 text-xs text-slate-400">Nenhum grupo criado.</p>
          ) : (
            grupos.map((g) => (
              <div key={g.id} className="group flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => onGrupoFilterChange(g.id)}
                  className={cn(
                    "flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors",
                    grupoFilter === g.id
                      ? "bg-violet-50 font-medium text-violet-700"
                      : "text-slate-600 hover:bg-slate-50",
                  )}
                >
                  <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{g.nome}</span>
                </button>
                {onExcluirGrupo && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onExcluirGrupo(g);
                    }}
                    className="rounded p-1 text-slate-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                    title={`Excluir grupo "${g.nome}"`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-auto border-t border-slate-100 px-4 py-4">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Usuário
        </p>
        <div className="flex items-center gap-2 rounded-lg px-1 py-1">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
            {initial}
          </div>
          <span className="truncate text-sm text-slate-700">{userName || "Você"}</span>
        </div>
      </div>
    </aside>
  );
}
